import { ColorEnum } from '@/constants/color'
import {
    formatMoneyInput,
    parseMoneyInput,
    toEditableMoneyInput,
} from '@/features/shiftCloseouts/utils/moneyInput'
import InputField from '@/features/ui/inputs/components/InputField'
import Typography from '@/features/ui/typography'
import { getCurrencySymbol } from '@/utils/priceHelper'
import { FC, useState } from 'react'
import { StyleProp, ViewStyle } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface AmountInputProps {
    accessibilityLabel: string
    allowNegative?: boolean
    appearance?: 'default' | 'subtle'
    currency: string
    disabled?: boolean
    error?: boolean
    onBlur?: () => void
    onChange?: (value: string) => void
    style?: StyleProp<ViewStyle>
    testID?: string
    value: string
}

const AmountInput: FC<AmountInputProps> = ({
    accessibilityLabel,
    allowNegative = false,
    appearance = 'default',
    currency,
    disabled = false,
    error = false,
    onBlur,
    onChange,
    style,
    testID,
    value,
}) => {
    const { styles } = useStyles(stylesheet)
    const [focused, setFocused] = useState(false)
    const [hasEdited, setHasEdited] = useState(false)

    const parsedValue = parseMoneyInput(value)
    const editableValue = hasEdited
        ? value
        : parsedValue === '0.00'
          ? ''
          : toEditableMoneyInput(value)
    const displayValue = focused ? editableValue : formatMoneyInput(value)
    const mutedZero = appearance === 'subtle' && !disabled && !error && parsedValue === '0.00'

    const handleChange = (nextValue: string) => {
        setHasEdited(true)
        onChange?.(nextValue)
    }

    const handleFocusChange = (nextFocused: boolean) => {
        setFocused(nextFocused)
        setHasEdited(false)

        if (!nextFocused) onBlur?.()
    }

    return (
        <InputField
            accessibilityLabel={accessibilityLabel}
            disabled={disabled}
            error={error}
            inputMode={allowNegative ? 'text' : 'decimal'}
            inputStyle={[
                styles.textInput,
                appearance === 'subtle' && styles.subtleTextInput,
                disabled && styles.disabledTextInput,
                mutedZero && styles.mutedTextInput,
            ]}
            keyboardType={allowNegative ? 'default' : 'decimal-pad'}
            onChange={handleChange}
            onFocusChange={handleFocusChange}
            style={[styles.input, disabled && styles.inputDisabled, style]}
            TailComponent={
                <Typography.Description
                    font="medium"
                    style={[
                        styles.currency,
                        appearance === 'default' && styles.defaultCurrency,
                        appearance === 'subtle' && styles.subtleCurrency,
                        disabled && styles.currencyDisabled,
                    ]}
                >
                    {getCurrencySymbol(currency)}
                </Typography.Description>
            }
            testID={testID}
            value={displayValue}
        />
    )
}

const stylesheet = createStyleSheet((theme) => ({
    input: {
        width: 120,
        height: 40,
        gap: 4,
    },
    inputDisabled: {
        width: 124,
        borderWidth: 0,
        backgroundColor: ColorEnum.VIOLET_100,
    },
    textInput: {
        minWidth: 0,
        paddingLeft: 12,
        paddingRight: 0,
        fontFamily: theme.fonts.medium,
        fontSize: 14,
        lineHeight: 16,
        textAlign: 'right',
    },
    subtleTextInput: {
        paddingLeft: 8,
    },
    disabledTextInput: {
        paddingLeft: 16,
    },
    mutedTextInput: {
        color: ColorEnum.GRAY_300,
    },
    currency: {
        paddingRight: 12,
        color: ColorEnum.GRAY_900,
    },
    defaultCurrency: {
        lineHeight: 16,
    },
    subtleCurrency: {
        color: ColorEnum.GRAY_600,
    },
    currencyDisabled: {
        color: ColorEnum.GRAY_400,
    },
}))

export default AmountInput
